<script setup lang="ts">
/**
 * This meal's reminders: when each is due, and a switch for this meal alone
 * (SPEC §10.23 — MT-156).
 *
 * Timing and audience are the group's, and this panel only says them. A meal
 * that could move its own reminders around would be a meal nobody could
 * predict; what one meal can do is go without a reminder, or take one the
 * group leaves off. "Changed for this meal" says when it has, with the way
 * back beside it.
 */
import { onMounted, ref } from 'vue'
import { endpoints, type EventReminderRuleResponse } from '@/api'
import { audienceLabel, kindLabel } from '@/utils/reminders'

const props = defineProps<{
  groupId: string
  eventId: string
  /** The group's zone, so "6:00 PM" means the group's 6 PM. */
  timeZone: string
  /** The reader's clock, injectable so "already due" is testable. */
  now?: Date
}>()

const rules = ref<EventReminderRuleResponse[]>([])
const loaded = ref(false)
const failed = ref(false)
const busy = ref<string | null>(null)
const error = ref<string | null>(null)

function due(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: props.timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function past(iso: string): boolean {
  return new Date(iso).getTime() <= (props.now ?? new Date()).getTime()
}

async function set(rule: EventReminderRuleResponse, enabled: boolean | null): Promise<void> {
  busy.value = rule.kind
  error.value = null

  try {
    const updated = await endpoints.setEventReminder(
      props.groupId,
      props.eventId,
      rule.kind,
      enabled,
    )
    rules.value = rules.value.map((r) => (r.kind === updated.kind ? updated : r))
  } catch {
    error.value = 'That did not save. Try again in a moment.'
  } finally {
    busy.value = null
  }
}

onMounted(async () => {
  try {
    rules.value = await endpoints.eventReminderRules(props.groupId, props.eventId)
  } catch {
    failed.value = true
  } finally {
    loaded.value = true
  }
})
</script>

<template>
  <div data-testid="reminder-schedule">
    <p v-if="failed" class="schedule__note">The reminder schedule could not be loaded.</p>

    <ul v-else-if="loaded" class="schedule__list">
      <li
        v-for="rule in rules"
        :key="rule.kind"
        class="schedule__row"
        :data-testid="`schedule-${rule.kind}`"
      >
        <div class="schedule__text">
          <span class="schedule__name">{{ kindLabel(rule.kind) }}</span>
          <span class="schedule__meta">
            <template v-if="rule.enabled">
              <template v-if="past(rule.dueUtc)">Was due {{ due(rule.dueUtc) }}</template>
              <template v-else>{{ due(rule.dueUtc) }}</template>
              · {{ audienceLabel(rule.audience) }}
            </template>
            <template v-else>Not sent for this meal</template>
          </span>
          <span v-if="rule.overridden" class="schedule__changed" data-testid="schedule-overridden">
            Changed for this meal ·
            <button
              type="button"
              class="schedule__reset"
              :disabled="busy !== null"
              @click="set(rule, null)"
            >
              use the group's setting
            </button>
          </span>
        </div>

        <v-switch
          :model-value="rule.enabled"
          color="primary"
          hide-details
          inset
          density="compact"
          :loading="busy === rule.kind"
          :disabled="busy !== null"
          :aria-label="`${kindLabel(rule.kind)} for this meal`"
          @update:model-value="(value) => set(rule, Boolean(value))"
        />
      </li>
    </ul>

    <p v-if="error" class="schedule__error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.schedule__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.schedule__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.schedule__row:last-child {
  border-bottom: none;
}

.schedule__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.schedule__name {
  font-weight: 500;
}

.schedule__meta,
.schedule__changed,
.schedule__note {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

.schedule__reset {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  min-height: 24px;
}

.schedule__error {
  color: rgb(var(--v-theme-error));
  font-size: 0.875rem;
  margin: 8px 0 0;
}
</style>
